import { useMemo } from 'react';
import { Card, CardHeader, CardBody, Flex, Text } from '@backstage/ui';
import { type TrackDetailsData } from '../../hooks';
import { getAchievedLevelName } from './track-utils';
import { LEVEL_COLORS, NO_LEVEL_COLOR } from './track-level-colors';

const compactNumber = new Intl.NumberFormat(undefined, {
  notation: 'compact',
  maximumFractionDigits: 1,
});

/**
 * Card summarising how the track's entities are distributed across achieved
 * levels: a segmented bar plus a per-level legend with counts and shares.
 */
export const CurrentStatusCard = ({
  details,
}: {
  details: TrackDetailsData;
}) => {
  const total = details.results.length;
  const distribution = useMemo(() => {
    const counts = new Map<number, number>();
    for (const result of details.results) {
      counts.set(
        result.achievedLevel,
        (counts.get(result.achievedLevel) ?? 0) + 1,
      );
    }

    const sortedLevels = [...details.track.levels].sort(
      (a, b) => a.ordinal - b.ordinal,
    );
    const colorByOrdinal = new Map<number, string>();
    sortedLevels.forEach((level, index) => {
      colorByOrdinal.set(
        level.ordinal,
        LEVEL_COLORS[index % LEVEL_COLORS.length],
      );
    });
    colorByOrdinal.set(0, NO_LEVEL_COLOR);

    // Show every track level (lowest first) plus "No Level" (0) last, even
    // when no entity has achieved that level.
    const displayOrdinals = [...sortedLevels.map(level => level.ordinal), 0];
    return displayOrdinals.map(ordinal => {
      const count = counts.get(ordinal) ?? 0;
      return {
        ordinal,
        name: getAchievedLevelName(details.track, ordinal),
        count,
        color: colorByOrdinal.get(ordinal) ?? NO_LEVEL_COLOR,
        fraction: total > 0 ? (count / total) * 100 : 0,
        percentage: total > 0 ? Math.round((count / total) * 100) : 0,
      };
    });
  }, [details, total]);

  return (
    <Card style={{ height: '100%' }}>
      <CardHeader>
        <Text variant="body-large">Current Status</Text>
      </CardHeader>
      <CardBody>
        {total === 0 ? (
          <Text color="secondary">No entities evaluated yet.</Text>
        ) : (
          <Flex
            direction="column"
            gap="4"
            justify="center"
            style={{ height: '100%' }}
          >
            <div
              style={{
                display: 'flex',
                height: '8px',
                borderRadius: 'var(--bui-radius-2)',
                overflow: 'hidden',
                backgroundColor: 'var(--bui-bg-neutral-2)',
              }}
            >
              {distribution.map(entry =>
                entry.fraction > 0 ? (
                  <div
                    key={entry.ordinal}
                    title={`${entry.name}: ${entry.percentage}%`}
                    style={{
                      width: `${entry.fraction}%`,
                      backgroundColor: entry.color,
                    }}
                  />
                ) : null,
              )}
            </div>
            <Flex direction="column" gap="2">
              {distribution.map(entry => (
                <Flex key={entry.ordinal} justify="between" align="center">
                  <Flex align="center" gap="2">
                    <span
                      style={{
                        display: 'inline-block',
                        width: '10px',
                        height: '10px',
                        borderRadius: 'var(--bui-radius-2)',
                        backgroundColor: entry.color,
                        flexShrink: 0,
                      }}
                    />
                    <Text variant="body-small">{entry.name}</Text>
                  </Flex>
                  <Text variant="body-small" color="secondary">
                    {compactNumber.format(entry.count)} of{' '}
                    {compactNumber.format(total)} ({entry.percentage}%)
                  </Text>
                </Flex>
              ))}
            </Flex>
          </Flex>
        )}
      </CardBody>
    </Card>
  );
};
